import React, { useState, useEffect } from "react";
import API_URL from "../settings";
import {
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  Paper,
  Checkbox,
  FormControlLabel,
  Divider,
  RadioGroup,
  Card,
  Radio,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import ADLSection from "./ADLSection";
import { calculateAdlScore } from "../utils/adlUtils";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ---------- Leaflet icon fix ---------- */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});
/* ------------------------------------- */

function MapClickHandler({ onPositionChange }) {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng);
    },
  });
  return null;
}

/* ---------- ADL helper ---------- */
const isAdlCompleted = (adl) =>
  Object.values(adl).every(v => v !== "");

/* -------------------------------- */

function AddPatient() {
  const navigate = useNavigate();

  const STEPS = [
    "ประวัติผู้สูงอายุ",
    "ที่อยู่ / การอยู่อาศัย",
    "สุขภาพพื้นฐาน",
    "พฤติกรรมสุขภาพ",
    "เศรษฐกิจ",
    "ประเมินภาวะซึมเศร้า",
    "ประเมินความเครียด",
    "ประเมิน ADL"
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [status, setStatus] = useState({});
  const [volunteers, setVolunteers] = useState([]);

  /* ================= FORM DATA ================= */
  const [formData, setFormData] = useState({
    // identity
    citizen_id: "",
    hn: "",
    first_name: "",
    last_name: "",
    gender: "",
    birth_date: "",
    age: "",
    phone: "",
    email: "",

    // status
    patient_type: "ELDERLY",
    marital_status: "",
    is_elderly: true,
    living_alone: false,
    bedridden_level: "",
    risk_group: "",

    // address detail
    house_no: "",
    village: "",
    alley: "",
    road: "",
    subdistrict: "",
    district: "",
    province: "",
    lat: "",
    lng: "",
    care_first_name: "",
    care_last_name: "",
    care_related: "",

    // health baseline
    blood_type: "",
    rh_factor: "",
    height_cm: "",
    weight_kg: "",
    allergy: "",
    surgery_history: "",
    symptom: "",
    chronic_diseases: [],
    family_disease_history: "",
    social_sup: "",
    //new fields
    disability: "",
    chronic_disease_note: "",
    regular_medications: "",
    primary_care_facility: "",

    // economy
    income_status: "",
    occupation: "",
    income_source: "",
    welfare_received: false,
    economic_notes: "",

    // relation
    volunteer_id: "",
    patient_image: "", // New field for base64 image
  });

  const [healthBehavior, setHealthBehavior] = useState({
    exercise_freq: "",
    exercise_type: "None",

    diet_taste: [],

    smoking: "No",
    smoke_per_day: "",

    alcohol: "No",
    alcohol_freq: "",

    temperament: [],
    self_treatment: []
  });

  /* ================= ADL ================= */
  const [adl, setAdl] = useState({
    feeding: "",
    grooming: "",
    transfer: "",
    toilet_use: "",
    mobility: "",
    dressing: "",
    stairs: "",
    bathing: "",
    bowels: "",
    bladder: ""
  });

  /* ================= DEPRESSION (2Q / 9Q) ================= */
  const [depression, setDepression] = useState({
    answer2q_1: null, // true / false
    answer2q_2: null, // true / false
    // 9Q
    answer9q_1: 0,
    answer9q_2: 0,
    answer9q_3: 0,
    answer9q_4: 0,
    answer9q_5: 0,
    answer9q_6: 0,
    answer9q_7: 0,
    answer9q_8: 0,
    answer9q_9: 0,
  });

  const is2QPositive =
    depression.answer2q_1 === true ||
    depression.answer2q_2 === true;

  /* ================= STRESS (ST-5) ================= */
  const [stress, setStress] = useState({
    answer_st5_1: 0,
    answer_st5_2: 0,
    answer_st5_3: 0,
    answer_st5_4: 0,
    answer_st5_5: 0,
  });

  /* ================= MAP ================= */
  const mapPosition =
    formData.lat && formData.lng
      ? [Number(formData.lat), Number(formData.lng)]
      : [13.7563, 100.5018];

  /* ================= LOAD VOLUNTEERS ================= */
  useEffect(() => {
    fetch(`${API_URL}/users?role=VOLUNTEER`)
      .then((res) => res.json())
      .then((data) => setVolunteers(data));
  }, []);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMapClick = (latlng) => {
    setFormData((prev) => ({
      ...prev,
      lat: latlng.lat.toFixed(6),
      lng: latlng.lng.toFixed(6),
    }));
  };

  const handleGetCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition((pos) => {
      setFormData((prev) => ({
        ...prev,
        lat: pos.coords.latitude.toFixed(6),
        lng: pos.coords.longitude.toFixed(6),
      }));
    });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          patient_image: reader.result // Base64 string
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        patient_image: ""
      }));
    }
  };

  const isValidCitizenId = (id) => {
    if (!/^\d{13}$/.test(id)) return false;
    let sum = 0;
    for (let i = 0; i < 12; i++) sum += parseInt(id[i]) * (13 - i);
    return (11 - (sum % 11)) % 10 === parseInt(id[12]);
  };

  const checkCitizenIdExists = async (citizenId) => {
    const res = await fetch(`${API_URL}/patients?citizen_id=${citizenId}`);
    const data = await res.json();
    return data.length > 0;
  };

  /* ================= STEP VALIDATION ================= */
  const validateStep = async () => {
    setStatus({});
    if (1 == 2) {
      if (activeStep === 0) {
        if (!formData.first_name || !formData.last_name) {
          setStatus({ type: "error", message: "กรุณากรอกชื่อ-นามสกุล" });
          return false;
        }

        if (formData.citizen_id) {
          if (!isValidCitizenId(formData.citizen_id)) {
            setStatus({ type: "error", message: "เลขบัตรประชาชนไม่ถูกต้อง" });
            return false;
          }

          const exists = await checkCitizenIdExists(formData.citizen_id);
          if (exists) {
            setStatus({ type: "error", message: "เลขบัตรประชาชนนี้มีอยู่แล้ว" });
            return false;
          }
        }
      }

      if (activeStep === 5 && !isAdlCompleted(adl)) {
        setStatus({ type: "error", message: "กรุณาประเมิน ADL ให้ครบทุกข้อ" });
        return false;
      }
    }

    return true;
  };

  const toggleArrayValue = (field, value) => {
    setHealthBehavior(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleNext = async () => {
    if (await validateStep()) setActiveStep((s) => s + 1);
  };

  const handleBack = () => setActiveStep((s) => s - 1);

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const { total, group } = calculateAdlScore(adl);
    const { score, label } = calculate9QResult(depression);
    const { score: stScore, label: stLabel } = calculateST5Result(stress);

    const payload = {
      citizen_id: formData.citizen_id || null,
      hn: formData.hn || null,
      first_name: formData.first_name,
      last_name: formData.last_name,
      gender: formData.gender,
      birth_date: formData.birth_date || null,
      age: Number(formData.age) || null,

      patient_type: "ELDERLY",
      marital_status: formData.marital_status || null,
      is_elderly: true,
      living_alone: formData.living_alone,
      bedridden_level: formData.bedridden_level || null,
      risk_group: formData.risk_group || null,

      social_sup: formData.social_sup || null,

      phone: formData.phone || null,
      email: formData.email || null,

      blood_type: formData.blood_type || null,
      rh_factor: formData.rh_factor || null,
      height_cm: Number(formData.height_cm) || null,
      weight_kg: Number(formData.weight_kg) || null,
      allergy: formData.allergy || null,
      surgery_history: formData.surgery_history || null,
      symptom: formData.symptom || null,
      chronic_disease: formData.chronic_diseases,
      family_disease_history: formData.family_disease_history || null,

      volunteer_id: formData.volunteer_id,

      address_detail: {
        house_no: formData.house_no || null,
        village: formData.village || null,
        alley: formData.alley || null,
        road: formData.road || null,
        subdistrict: formData.subdistrict || null,
        district: formData.district || null,
        province: formData.province || null,
        lat: formData.lat ? Number(formData.lat) : null,
        lng: formData.lng ? Number(formData.lng) : null,
      },

      economy: {
        status: formData.income_status || null,
        occupation: formData.occupation || null,
        source: formData.income_source || null,
        welfare: formData.welfare_received,
        notes: formData.economic_notes || null,
      },

      adl_summary: {
        latest_score: total,
        latest_group: group,
        last_assessed: new Date().toISOString().split("T")[0],
      },

      //Depression section
      depression: {
        answer2q_1: depression.answer2q_1,
        answer2q_2: depression.answer2q_2,
        result_2q: is2QPositive,

        ...(is2QPositive && {
          answer9q_1: depression.answer9q_1,
          answer9q_2: depression.answer9q_2,
          answer9q_3: depression.answer9q_3,
          answer9q_4: depression.answer9q_4,
          answer9q_5: depression.answer9q_5,
          answer9q_6: depression.answer9q_6,
          answer9q_7: depression.answer9q_7,
          answer9q_8: depression.answer9q_8,
          answer9q_9: depression.answer9q_9,
          result_9q_score: score,
          result_9q_label: label,
          assessed_date: new Date().toISOString().split("T")[0],
          assessed_by: formData.volunteer_id || null
        })
      },

      stress: {
        answer_st5_1: stress.answer_st5_1,
        nswer_st5_2: stress.answer_st5_2,
        answer_st5_3: stress.answer_st5_3,
        answer_st5_4: stress.answer_st5_4,
        answer_st5_5: stress.answer_st5_5,
        result_st5_score: stScore,
        result_st5_label: stLabel,
        assessed_date: new Date().toISOString().split("T")[0],
        assessed_by: formData.volunteer_id || null
      },

      patient_image: formData.patient_image || null, // Include image in payload

      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const patientRes = await fetch(`${API_URL}/patients`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const patient = await patientRes.json();

    await fetch(`${API_URL}/health_behaviors`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        patient_id: patient.id,

        exercise_freq: Number(healthBehavior.exercise_freq) || 0,
        exercise_type: healthBehavior.exercise_type,

        diet_taste: healthBehavior.diet_taste,

        smoking: healthBehavior.smoking,
        smoke_per_day: Number(healthBehavior.smoke_per_day) || 0,

        alcohol: healthBehavior.alcohol,
        alcohol_freq: Number(healthBehavior.alcohol_freq) || 0,

        temperament: healthBehavior.temperament,
        self_treatment: healthBehavior.self_treatment,

        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
    });

    await handleSaveAdl(patient.id);

    setStatus({ type: 'success', message: 'บันทึกข้อมูลเรียบร้อย' });
    navigate("/patients");
  };

  const handleSaveAdl = async (id) => {
    if (!isAdlCompleted(adl)) {
      throw new Error('ADL ไม่ครบ');
    }

    const { total, group } = calculateAdlScore(adl);

    await fetch(`${API_URL}/adl_records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patient_id: id ?? null,
        assessment_date: new Date().toISOString().split('T')[0],
        ...adl,
        total_score: total,
        adl_group: group,
        assessed_by: formData.volunteer_id || null,
        created_at: new Date().toISOString(),
      })
    });

    await fetch(`${API_URL}/patients/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        adl_summary: {
          latest_score: total,
          latest_group: group,
          last_assessed: new Date().toISOString().split('T')[0],
        }
      })
    });

    return true;
  };

  const calculate9QResult = (dep) => {
    const score = [
      dep.answer9q_1,
      dep.answer9q_2,
      dep.answer9q_3,
      dep.answer9q_4,
      dep.answer9q_5,
      dep.answer9q_6,
      dep.answer9q_7,
      dep.answer9q_8,
      dep.answer9q_9,
    ].reduce((sum, v) => sum + Number(v || 0), 0);

    let label = "ไม่มีอาการของโรคซึมเศร้า";
    if (score >= 7 && score <= 12)
      label = "มีอาการของโรคซึมเศร้า ระดับน้อย";
    else if (score >= 13 && score <= 18)
      label = "มีอาการของโรคซึมเศร้า ระดับปานกลาง";
    else if (score >= 19)
      label = "มีอาการของโรคซึมเศร้า ระดับรุนแรง";

    return { score, label };
  };

  const calculateST5Result = (st) => {
    const score = [
      st.answer_st5_1,
      st.answer_st5_2,
      st.answer_st5_3,
      st.answer_st5_4,
      st.answer_st5_5,
    ].reduce((s, v) => s + Number(v || 0), 0);

    let label = "ความเครียดน้อย";
    if (score >= 5 && score <= 7) label = "ความเครียดปานกลาง";
    else if (score >= 8 && score <= 9) label = "ความเครียดมาก";
    else if (score >= 10) label = "ความเครียดมากที่สุด";

    return { score, label };
  };

  /* ---------- render ---------- */
  return (
    <>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        เพิ่มข้อมูลผู้สูงอายุ
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 5 }}>
        <Box component="form" onSubmit={handleSubmit}>

          {activeStep === 0 && (
            <>
              {/* ================= Image Upload/Capture ================= */}
              <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CameraAltIcon />}
                  >
                    ถ่ายภาพ/อัปโหลดรูปผู้สูงอายุ
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      hidden
                      onChange={handleImageUpload}
                    />
                  </Button>
                  {formData.patient_image && (
                    <Box sx={{ mt: 2, maxWidth: 300 }}>
                      <img
                        src={formData.patient_image}
                        alt="Patient Preview"
                        style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                      />
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" color="error" onClick={() => setFormData(prev => ({ ...prev, patient_image: "" }))}>
                          ลบรูป
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* ================= ข้อมูลทั่วไป ================= */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="เลขบัตรประชาชน"
                    name="citizen_id"
                    value={formData.citizen_id}
                    onChange={handleChange}
                    inputProps={{ maxLength: 13 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ชื่อ"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="นามสกุล"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>เพศ</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <MenuItem value="M">ชาย</MenuItem>
                      <MenuItem value="F">หญิง</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="วันเกิด"
                    name="birth_date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.birth_date}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="อายุ"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="เบอร์โทรศัพท์"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>

              </Grid>

              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>เจ้าหน้าที่ อสม.</InputLabel>
                    <Select
                      value={formData.volunteer_id ?? ""}
                      label="อาสาสมัคร"
                      onChange={handleChange}
                      name="volunteer_id"
                    >
                      <MenuItem value="">
                        <em>ยังไม่กำหนด</em>
                      </MenuItem>
                      {volunteers.map(v => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="รพ.สต."
                    name="health_station"
                    value={formData.health_station}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

            </>)}


          {activeStep === 1 && (
            <>
              {/* ================= ที่อยู่อาศัย ================= */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="บ้านเลขที่"
                    name="house_no"
                    value={formData.house_no || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="หมู่"
                    name="village"
                    value={formData.village || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ซอย"
                    name="alley"
                    value={formData.alley || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ถนน"
                    name="road"
                    value={formData.road || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ตำบล / แขวง"
                    name="subdistrict"
                    value={formData.subdistrict || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="อำเภอ / เขต"
                    name="district"
                    value={formData.district || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="จังหวัด"
                    name="province"
                    value={formData.province || ""}
                    onChange={handleChange}
                  />
                </Grid>

                {/* สถานะการอยู่อาศัย / สังคม */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="living_alone"
                        checked={formData.living_alone}
                        onChange={handleChange}
                      />
                    }
                    label="อาศัยอยู่คนเดียว"
                  />
                </Grid>


                <Grid size={{ xs: 12, md: 12 }}>
                  <Typography variant="p" sx={{ mb: 2, fontWeight: 'bold' }}>
                    ข้อมูลผู้ดูแล
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ชื่อ"
                    name="care_first_name"
                    value={formData.care_first_name || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="นามสกุล"
                    name="care_last_name"
                    value={formData.care_last_name || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ความสัมพันธ์"
                    name="care_related"
                    value={formData.care_related || ""}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              {/* ================= MAP ================= */}
              <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 2 }}>
                <Typography variant="p" sx={{ mb: 2, fontWeight: 'bold' }}>
                  ที่ตั้งในแผนที่
                </Typography>
              </Grid>

              <Box sx={{ mt: 2, height: 400 }}>
                <MapContainer
                  key={`${mapPosition[0]}-${mapPosition[1]}`}
                  center={mapPosition}
                  zoom={formData.lat ? 20 : 12}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution="&copy; OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler onPositionChange={handleMapClick} />
                  {formData.lat && <Marker position={mapPosition} />}
                </MapContainer>
              </Box>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {/* 🔹 ปุ่มตำแหน่งปัจจุบัน */}
                <Grid size={{ xs: 12, md: 12, mt: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MyLocationIcon />}
                    onClick={handleGetCurrentLocation}
                  >
                    ใช้ตำแหน่งปัจจุบัน
                  </Button>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    name="lat"
                    value={formData.lat}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    name="lng"
                    value={formData.lng}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </>
          )}

          {activeStep === 2 && (
            <>
              {/* ================= โรคประจำตัว ================= */}
              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth sx={{ '& .MuiInputBase-root': { height: 56 } }}>
                    <InputLabel>กรุ๊ปเลือด</InputLabel>
                    <Select
                      name="blood_type"
                      value={formData.blood_type}
                      onChange={handleChange}
                    >
                      <MenuItem value="A">A</MenuItem>
                      <MenuItem value="B">B</MenuItem>
                      <MenuItem value="AB">AB</MenuItem>
                      <MenuItem value="O">O</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth sx={{ '& .MuiInputBase-root': { height: 56 } }}>
                    <InputLabel>Rh</InputLabel>
                    <Select
                      name="rh_factor"
                      value={formData.rh_factor}
                      onChange={handleChange}
                    >
                      <MenuItem value="+">Rh+</MenuItem>
                      <MenuItem value="-">Rh-</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ส่วนสูง (ซม.)"
                    name="height_cm"
                    type="number"
                    value={formData.height_cm}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="น้ำหนัก (กก.)"
                    name="weight_kg"
                    type="number"
                    value={formData.weight_kg}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 9 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="ประวัติการผ่าตัด"
                    name="surgery_history"
                    value={formData.surgery_history || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="ประวัติการแพ้ยา / อาหาร"
                    name="allergy"
                    value={formData.allergy}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="ประวัติโรคในครอบครัว"
                    name="family_disease_history"
                    value={formData.family_disease_history || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="อาการปัจจุบัน"
                    name="symptom"
                    value={formData.symptom || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="ความพิการ"
                    name="disability"
                    value={formData.disability || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="โรคประจำตัว"
                    name="chronic_disease_note"
                    value={formData.chronic_disease_note || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="ยาที่รับประทานประจำ"
                    name="regular_medications"
                    value={formData.regular_medications || ""}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="สถานพยาบาลที่รักษา"
                    name="primary_care_facility"
                    value={formData.primary_care_facility || ""}
                    onChange={handleChange}
                  />
                </Grid>


                <Grid size={{ xs: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="การได้รับผ้าอ้อม"
                    name="social_sup"
                    value={formData.social_sup || ""}
                    onChange={handleChange}
                  />
                </Grid>

              </Grid>
            </>)}

          {activeStep === 3 && (
            <>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                พฤติกรรมสุขภาพ
              </Typography>

              <Grid container spacing={3}>

                {/* การออกกำลังกาย */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    fullWidth
                    label="ออกกำลังกาย (ครั้ง/สัปดาห์)"
                    type="number"
                    value={healthBehavior.exercise_freq}
                    onChange={(e) =>
                      setHealthBehavior(prev => ({
                        ...prev,
                        exercise_freq: e.target.value
                      }))
                    }
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>การออกกำลังกาย</InputLabel>
                    <Select
                      value={healthBehavior.exercise_type}
                      onChange={(e) =>
                        setHealthBehavior(prev => ({
                          ...prev,
                          exercise_type: e.target.value
                        }))
                      }
                    >
                      <MenuItem value="Exercise">ออกกำลังกาย</MenuItem>
                      <MenuItem value="None">ไม่ออกกำลังกาย</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* รสนิยมอาหาร */}
                <Grid size={{ xs: 12 }}>
                  <Typography fontWeight="bold">รสนิยมอาหาร</Typography>
                  {["Salty : เค็ม", "Sweet : หวาน", "Sour : เปรี้ยว", "Spicy : เผ็ด", "Bland : จืด"].map(v => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={healthBehavior.diet_taste.includes(v)}
                          onChange={() => toggleArrayValue("diet_taste", v)}
                        />
                      }
                      label={v}
                    />
                  ))}
                </Grid>

                {/* บุหรี่ */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>การสูบบุหรี่</InputLabel>
                    <Select
                      value={healthBehavior.smoking}
                      onChange={(e) =>
                        setHealthBehavior(prev => ({
                          ...prev,
                          smoking: e.target.value
                        }))
                      }
                    >
                      <MenuItem value="No">ไม่สูบ</MenuItem>
                      <MenuItem value="Yes">สูบ</MenuItem>
                      <MenuItem value="Quit">เลิกแล้ว</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {healthBehavior.smoking === "Yes" && (
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="จำนวนมวน/วัน"
                      type="number"
                      value={healthBehavior.smoke_per_day}
                      onChange={(e) =>
                        setHealthBehavior(prev => ({
                          ...prev,
                          smoke_per_day: e.target.value
                        }))
                      }
                    />
                  </Grid>
                )}

                {/* แอลกอฮอล์ */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>การดื่มสุรา</InputLabel>
                    <Select
                      value={healthBehavior.alcohol}
                      onChange={(e) =>
                        setHealthBehavior(prev => ({
                          ...prev,
                          alcohol: e.target.value
                        }))
                      }
                    >
                      <MenuItem value="No">ไม่ดื่ม</MenuItem>
                      <MenuItem value="Occasional">ดื่มบางครั้ง</MenuItem>
                      <MenuItem value="Yes">ดื่มประจำ</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {healthBehavior.alcohol !== "No" && (
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="จำนวนครั้ง/สัปดาห์"
                      type="number"
                      value={healthBehavior.alcohol_freq}
                      onChange={(e) =>
                        setHealthBehavior(prev => ({
                          ...prev,
                          alcohol_freq: e.target.value
                        }))
                      }
                    />
                  </Grid>
                )}

                {/* อุปนิสัย */}
                <Grid size={{ xs: 12 }}>
                  <Typography fontWeight="bold">อุปนิสัย</Typography>
                  {["Calm : ใจเย็น", "Irritable : หงุดหงิดง่าย", "Cheerful : ร่าเริง", "Stressful : เครียดง่าย"].map(v => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={healthBehavior.temperament.includes(v)}
                          onChange={() => toggleArrayValue("temperament", v)}
                        />
                      }
                      label={v}
                    />
                  ))}
                </Grid>

                {/* การดูแลตนเอง */}
                <Grid size={{ xs: 12 }}>
                  <Typography fontWeight="bold">การดูแลตนเองเมื่อเจ็บป่วย</Typography>
                  {["BuyMedicine", "LocalHealer", "HealthCenter", "Hospital"].map(v => (
                    <FormControlLabel
                      key={v}
                      control={
                        <Checkbox
                          checked={healthBehavior.self_treatment.includes(v)}
                          onChange={() => toggleArrayValue("self_treatment", v)}
                        />
                      }
                      label={v}
                    />
                  ))}
                </Grid>

              </Grid>
            </>
          )}


          {activeStep === 4 && (
            <>
              {/* ================= เศรษฐกิจ ================= */}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControl fullWidth sx={{ '& .MuiInputBase-root': { height: 56 } }}>
                    <InputLabel>สถานะรายได้</InputLabel>
                    <Select name="income_status"
                      value={formData.income_status} onChange={handleChange}>
                      <MenuItem value="มี">มี</MenuItem>
                      <MenuItem value="รายได้น้อย">รายได้น้อย</MenuItem>
                      <MenuItem value="ไม่มี">ไม่มี</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField fullWidth label="อาชีพ" name="occupation"
                    value={formData.occupation} onChange={handleChange} />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField fullWidth label="แหล่งรายได้หลัก" name="income_source"
                    value={formData.income_source} onChange={handleChange} />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="welfare_received"
                        checked={formData.welfare_received}
                        onChange={handleChange}
                      />
                    }
                    label="ได้รับสวัสดิการจากรัฐ"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="บันทึกเพิ่มเติม (ด้านเศรษฐกิจ)"
                    name="economic_notes"
                    value={formData.economic_notes}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </>)}


          {activeStep === 5 && (
            <>
              <Typography fontWeight="bold">แบบคัดกรองโรคซึมเศร้า </Typography>
              <Divider sx={{ my: 2 }} />
              {/* ================= 2Q ================= */}
              {[
                { key: "answer2q_1", text: "ใน 2 สัปดาห์ที่ผ่านมา รู้สึกเศร้า ท้อแท้ หรือไม่" },
                { key: "answer2q_2", text: "ใน 2 สัปดาห์ที่ผ่านมา เบื่อ ไม่อยากทำอะไร หรือไม่" }
              ].map(q => (
                <Box key={q.key} sx={{ mt: 2 }}>
                  <Card
                    key={q.key}
                    sx={{
                      p: 2,
                      my: 2,
                      opacity: 1
                    }}
                  >
                    <Typography>{q.text}</Typography>

                    <RadioGroup
                      row
                      value={depression[q.key]}
                      onChange={(e) =>
                        setDepression(prev => ({
                          ...prev,
                          [q.key]: e.target.value === "true"
                        }))
                      }
                    >
                      <FormControlLabel value="true" control={<Radio />} label="มี" />
                      <FormControlLabel value="false" control={<Radio />} label="ไม่มี" />
                    </RadioGroup>
                  </Card>
                </Box>
              ))}

              <Typography sx={{ mt: 2 }} fontWeight="bold">แบบประเมินโรคซึมเศร้า </Typography>
              <Divider sx={{ my: 2 }} />

              {is2QPositive && (
                <>
                  {[
                    "เบื่อ ไม่สนใจอยากทำอะไร",
                    "ไม่สบายใจ ซึมเศร้า ท้อแท้",
                    "หลับยากหรือหลับมากไป",
                    "เหนื่อยง่าย หรือไม่ค่อยมีแรง",
                    "เบื่ออาหารหรือกินมากไป",
                    "รู้สึกไม่ดีกับตนเอง",
                    "สมาธิไม่ดี",
                    "พูดช้าหรือกระสับกระส่าย",
                    "คิดทำร้ายตนเอง"
                  ].map((label, idx) => {
                    const key = `answer9q_${idx + 1}`;
                    return (
                      <Box key={key} sx={{ mt: 2 }}>
                        <Card
                          key={key}
                          sx={{
                            p: 2,
                            my: 2,
                            opacity: 1
                          }}
                        >
                          <Typography fontWeight="bold">{label}</Typography>

                          <RadioGroup
                            row
                            value={depression[key]}
                            onChange={(e) =>
                              setDepression(prev => ({
                                ...prev,
                                [key]: Number(e.target.value)
                              }))
                            }
                          >
                            <FormControlLabel value={0} control={<Radio />} label="ไม่มี (0)" />
                            <FormControlLabel value={1} control={<Radio />} label="บางวัน (1)" />
                            <FormControlLabel value={2} control={<Radio />} label="บ่อย >7 วัน (2)" />
                            <FormControlLabel value={3} control={<Radio />} label="ทุกวัน (3)" />
                          </RadioGroup>
                        </Card>
                      </Box>
                    );
                  })}

                  {/* ===== Summary แบบ ADL ===== */}
                  {(() => {
                    const { score, label } = calculate9QResult(depression);
                    return (
                      <Alert severity="info" sx={{ mt: 3 }}>
                        คะแนนรวม {score} คะแนน — <strong>{label}</strong>
                      </Alert>
                    );
                  })()}
                </>
              )}
            </>
          )}

          {activeStep === 6 && (
            <>
              <Typography variant="h6" fontWeight="bold" sx={{ mt: 3 }}>
                แบบประเมินความเครียด (ST-5)
              </Typography>
              <Divider sx={{ my: 2 }} />

              {[
                "มีปัญหาการนอน นอนไม่หลับหรือนอนมาก",
                "มีสมาธิน้อยลง",
                "หงุดหงิด / กระวนกระวาย / ว้าวุ่นใจ",
                "รู้สึกเบื่อ เซ็ง",
                "ไม่อยากพบปะผู้คน"
              ].map((label, idx) => {
                const key = `answer_st5_${idx + 1}`;
                return (
                  <Card key={key} sx={{ p: 2, my: 2 }}>
                    <Typography fontWeight="bold">{idx + 1}. {label}</Typography>
                    <RadioGroup
                      row
                      value={stress[key]}
                      onChange={(e) =>
                        setStress(prev => ({
                          ...prev,
                          [key]: Number(e.target.value)
                        }))
                      }
                    >
                      <FormControlLabel value={0} control={<Radio />} label="แทบไม่มี (0)" />
                      <FormControlLabel value={1} control={<Radio />} label="บางครั้ง (1)" />
                      <FormControlLabel value={2} control={<Radio />} label="บ่อยครั้ง (2)" />
                      <FormControlLabel value={3} control={<Radio />} label="เป็นประจำ (3)" />
                    </RadioGroup>
                  </Card>
                );
              })}

              {/* ===== Summary ===== */}
              {(() => {
                const { score, label } = calculateST5Result(stress);
                return (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    คะแนนรวม {score} คะแนน — <strong>{label}</strong>
                  </Alert>
                );
              })()}

            </>
          )}


          {activeStep === 7 && (
            <>
              {/* ================= ADL ================= */}
              <ADLSection adl={adl} setAdl={setAdl} />
            </>
          )}

          {status.message && (
            <Alert severity={status.type} sx={{ mt: 2 }}>
              {status.message}
            </Alert>
          )}

          {/* ================= BUTTON ================= */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              ย้อนกลับ
            </Button>

            {activeStep < STEPS.length - 1 ? (
              <Button variant="contained" onClick={handleNext}>
                ถัดไป
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={handleSubmit}
              >
                บันทึกข้อมูล
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );
}

export default AddPatient;